import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DrawingEditor } from "@/components/DrawingEditor";
import { ImageGallery, type GalleryImage } from "@/components/ImageGallery";
import { AdminPanel } from "@/components/AdminPanel";
import { SpendingStats } from "@/components/SpendingStats";
import { SmartPromptWizard } from "@/components/SmartPromptWizard";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { PersonGarmentLibrary } from "@/components/PersonGarmentLibrary";
import { PersonGarmentPicker } from "@/components/PersonGarmentPicker";
import { PromptGuards } from "@/components/PromptGuards";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { ImagePlus, X, Download, Loader2, Pencil, Settings2, Image as ImageIcon, LogOut, Shield, User, Sparkles, PenTool, Sun, Moon, DollarSign, Globe, Wand2, Columns2, BookOpen, ArrowUpCircle, Check, ChevronsUpDown, Shirt, UserRound, AlertTriangle, Dices, Eraser } from "lucide-react";
import { useState, useRef, useEffect, useMemo, type DragEvent } from "react";
import { nanoid } from "nanoid";
import { useTheme } from "@/hooks/useTheme";
import { MODELS } from "@/models";
import { buildGuardedPrompt, loadGuards, GUARDS_STORAGE_KEY } from "@/lib/promptGuards";
import { cycleRole, syncRoles, applyTransferRoles, type RoleMap } from "@/lib/transferRoles";

function priceTier(cost: number): number {
  if (cost <= 0.04) return 1;
  if (cost <= 0.06) return 2;
  if (cost <= 0.10) return 3;
  if (cost <= 0.15) return 4;
  return 5;
}

type Mode = "edit" | "create";

const ASPECT_RATIOS = [
  { id: "auto", label: "Auto", w: 1, h: 1, hint: "Auto" },
  { id: "1:1", label: "1:1", w: 1, h: 1, hint: "Profil" },
  { id: "21:9", label: "21:9", w: 21, h: 9, hint: "Cinema" },
  { id: "16:9", label: "16:9", w: 16, h: 9, hint: "YouTube" },
  { id: "3:2", label: "3:2", w: 3, h: 2, hint: "DSLR" },
  { id: "4:3", label: "4:3", w: 4, h: 3, hint: "iPad" },
  { id: "5:4", label: "5:4", w: 5, h: 4, hint: "Print" },
  { id: "4:5", label: "4:5", w: 4, h: 5, hint: "IG Post" },
  { id: "3:4", label: "3:4", w: 3, h: 4, hint: "Portret" },
  { id: "2:3", label: "2:3", w: 2, h: 3, hint: "Pinterest" },
  { id: "9:16", label: "9:16", w: 9, h: 16, hint: "IG Story" },
  { id: "4:1", label: "4:1", w: 4, h: 1, hint: "Banner" },
  { id: "1:4", label: "1:4", w: 1, h: 4, hint: "Skyscraper" },
  { id: "8:1", label: "8:1", w: 8, h: 1, hint: "Ultra širok" },
  { id: "1:8", label: "1:8", w: 1, h: 8, hint: "Ultra visok" },
] as const;

const NEON_COLORS = ["#00ffcc", "#9d00ff", "#ff0077", "#ffcc00", "#00aaff"];

function computeImageSize(ratioId: string, resId: string, resolutions: { id: string; maxSide: number }[]): { width: number; height: number } {
  const ratio = ASPECT_RATIOS.find((r) => r.id === ratioId) ?? ASPECT_RATIOS[0];
  const res = resolutions.find((r) => r.id === resId) ?? resolutions[resolutions.length - 1];
  const max = res.maxSide;
  if (ratio.w >= ratio.h) {
    return { width: max, height: Math.round(max * ratio.h / ratio.w) };
  }
  return { width: Math.round(max * ratio.w / ratio.h), height: max };
}

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  /** Black-and-white removal mask (base64 PNG), drawn in the mask editor */
  mask?: string;
}

interface FalImageEditorProps {
  username: string;
  isAdmin: boolean;
  onLogout: () => void;
}

export function FalImageEditor({ username, isAdmin, onLogout }: FalImageEditorProps) {
  const { theme, toggleTheme } = useTheme();
  const [selectedModelId, setSelectedModelId] = useState(() => {
    const saved = localStorage.getItem("lunatik-model");
    return saved && MODELS.some((m) => m.id === saved) ? saved : "seedream-v5-lite";
  });
  const [mode, setMode] = useState<Mode>("edit");
  const [images, setImages] = useState<ImageFile[]>([]);
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("3:4");
  const [resolution, setResolution] = useState("4K");
  const [safetyChecker, setSafetyChecker] = useState(false);
  const [safetyTolerance, setSafetyTolerance] = useState("6");
  const [limitGenerations, setLimitGenerations] = useState(true);
  const [enhancePromptMode, setEnhancePromptMode] = useState<"standard" | "fast">("standard");
  const [numImages, setNumImages] = useState(1);
  const [webSearch, setWebSearch] = useState(false);
  const [showSmartPrompt, setShowSmartPrompt] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [beforeImage, setBeforeImage] = useState<string | null>(null);
  const [showCompare, setShowCompare] = useState(false);
  // Upscaler-specific state (shared)
  const [upscaleFactor, setUpscaleFactor] = useState(2);
  const [outputFormat, setOutputFormat] = useState("png");
  const [isUpscaling, setIsUpscaling] = useState(false);
  // SeedVR-specific
  const [upscaleMode, setUpscaleMode] = useState<"target" | "factor">("factor");
  const [targetResolution, setTargetResolution] = useState("1080p");
  const [noiseScale, setNoiseScale] = useState(0.1);
  // Topaz-specific
  const [upscalerVariant, setUpscalerVariant] = useState("Standard V2");
  const [faceEnhancement, setFaceEnhancement] = useState(true);
  const [faceEnhancementStrength, setFaceEnhancementStrength] = useState(0.8);
  const [faceEnhancementCreativity, setFaceEnhancementCreativity] = useState(0);
  const [subjectDetection, setSubjectDetection] = useState("All");
  // Qwen / general generation options
  const [negativePrompt, setNegativePrompt] = useState(() => {
    const saved = localStorage.getItem("lunatik-model");
    const model = saved ? MODELS.find((m) => m.id === saved) : null;
    return model?.defaultNegativePrompt || "";
  });
  const [generationOutputFormat, setGenerationOutputFormat] = useState("jpeg");
  const [enablePromptExpansion, setEnablePromptExpansion] = useState(true);
  const [quality, setQuality] = useState("high");
  // Seed ("" = random, model picks its own)
  const [seed, setSeed] = useState("");
  // Prompt guards + transfer roles
  const [guards, setGuards] = useState<string[]>(() => loadGuards());
  const [roles, setRoles] = useState<RoleMap>({});
  const [modelPickerOpen, setModelPickerOpen] = useState(false);

  const selectedModel = MODELS.find((m) => m.id === selectedModelId) ?? MODELS[0];
  const requiresMask = !!selectedModel.requiresMask;
  /** Models that run without a prompt: upscalers and mask-based removal */
  const isPromptless = selectedModel.isUpscaler || requiresMask;

  const updateGuards = (ids: string[]) => {
    setGuards(ids);
    localStorage.setItem(GUARDS_STORAGE_KEY, JSON.stringify(ids));
  };

  // Keep roles in sync with the strip: prune removed images, seed defaults
  useEffect(() => {
    setRoles((prev) => {
      const next = syncRoles(images, prev);
      const same =
        Object.keys(next).length === Object.keys(prev).length &&
        Object.keys(next).every((k) => next[k] === prev[k]);
      return same ? prev : next;
    });
  }, [images]);

  // The exact prompt the model will receive, kept live for the preview panel
  const { ordered: orderedImages, sentence: transferSentence } = useMemo(
    () => applyTransferRoles(images, roles),
    [images, roles]
  );
  const guardsApply = mode === "edit" && !isPromptless;
  const { finalPrompt, negativeAdditions } = useMemo(() => {
    const base = [prompt.trim(), transferSentence].filter(Boolean).join(" ");
    if (!guardsApply) return { finalPrompt: base, negativeAdditions: "" };
    return buildGuardedPrompt(base, guards, !!selectedModel.hasNegativePrompt);
  }, [prompt, transferSentence, guards, guardsApply, selectedModel.hasNegativePrompt]);

  const handleModelChange = (modelId: string) => {
    const model = MODELS.find((m) => m.id === modelId);
    if (!model) return;
    setSelectedModelId(modelId);
    localStorage.setItem("lunatik-model", modelId);
    // Upscaler always uses edit mode
    if (model.isUpscaler) {
      setMode("edit");
      return;
    }
    // Mask-based removal works on exactly one image
    if (model.requiresMask) {
      setMode("edit");
      setImages((prev) => {
        if (prev.length <= 1) return prev;
        prev.slice(0, -1).forEach((img) => URL.revokeObjectURL(img.preview));
        return prev.slice(-1);
      });
      return;
    }
    // Switch to create mode if model doesn't support edit
    if (!model.supportsEdit && mode === "edit") {
      setMode("create");
    }
    // Switch to edit mode if model doesn't support create
    if (!model.supportsCreate && mode === "create") {
      setMode("edit");
    }
    // Reset aspect ratio if current one isn't supported
    if (!model.aspectRatios.includes(aspectRatio)) {
      setAspectRatio(model.aspectRatios[0]);
    }
    // Reset resolution if current one isn't available
    if (model.resolutions.length && !model.resolutions.find((r) => r.id === resolution)) {
      const autoRes = model.resolutions.find((r) => r.id === "auto" || r.autoValue === "auto");
      setResolution(autoRes ? autoRes.id : model.resolutions[model.resolutions.length - 1]!.id);
    }
    // Set default negative prompt
    if (model.hasNegativePrompt && model.defaultNegativePrompt) {
      setNegativePrompt(model.defaultNegativePrompt);
    } else {
      setNegativePrompt("");
    }
    // Set default generation output format
    if (model.generationOutputFormats?.length) {
      setGenerationOutputFormat(model.generationOutputFormats[0].id);
    }
    // Set default quality
    if (model.hasQuality && model.qualityOptions?.length) {
      setQuality("high");
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSpending, setShowSpending] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedGarmentId, setSelectedGarmentId] = useState<number | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [libraryFocusPersonId, setLibraryFocusPersonId] = useState<number | null>(null);
  const [libraryFocusGarmentId, setLibraryFocusGarmentId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const infinityPathRef = useRef<SVGPathElement>(null);
  const neonColorIndexRef = useRef(0);
  const [neonColor, setNeonColor] = useState(NEON_COLORS[0]);
  const [infinityPathLength, setInfinityPathLength] = useState(300);

  const editingImage = images.find((img) => img.id === editingImageId);

  // Calculate infinity path length when loading starts
  useEffect(() => {
    if (isLoading && infinityPathRef.current) {
      setInfinityPathLength(infinityPathRef.current.getTotalLength());
    }
    if (isLoading) {
      neonColorIndexRef.current = 0;
      setNeonColor(NEON_COLORS[0]);
    }
  }, [isLoading]);

  // Cycle neon color on each progress message change
  useEffect(() => {
    if (progressMessage && isLoading) {
      neonColorIndexRef.current = (neonColorIndexRef.current + 1) % NEON_COLORS.length;
      setNeonColor(NEON_COLORS[neonColorIndexRef.current]);
    }
  }, [progressMessage, isLoading]);

  // Fetch gallery images on mount
  useEffect(() => {
    fetch("/api/images")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setGalleryImages(data))
      .catch(() => {});
  }, []);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const newImages: ImageFile[] = [];
    for (const file of files) {
      if (file.type.startsWith("image/")) {
        newImages.push({
          id: nanoid(),
          file,
          preview: URL.createObjectURL(file),
        });
      }
    }
    setImages((prev) => {
      // Mask-based removal keeps a single source image — a new upload replaces it
      if (requiresMask) {
        const next = newImages.slice(-1);
        if (next.length === 0) return prev;
        prev.forEach((img) => URL.revokeObjectURL(img.preview));
        return next;
      }
      return [...prev, ...newImages];
    });
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  const addImageForUpscale = async (imageUrl: string) => {
    try {
      const res = await fetch(imageUrl);
      if (!res.ok) return;
      const blob = await res.blob();
      const file = new File([blob], "upscale.png", { type: blob.type || "image/png" });
      setImages([{ id: nanoid(), file, preview: URL.createObjectURL(blob) }]);
      handleModelChange("topaz");
    } catch { /* ignore */ }
  };

  const addImageFromUrl = async (imageUrl: string) => {
    try {
      const res = await fetch(imageUrl);
      if (!res.ok) return;
      const blob = await res.blob();
      const file = new File([blob], "reference.png", { type: blob.type || "image/png" });
      const next = { id: nanoid(), file, preview: URL.createObjectURL(blob) };
      setImages((prev) => {
        // Mask-based removal holds a single source image
        if (requiresMask) {
          prev.forEach((img) => URL.revokeObjectURL(img.preview));
          return [next];
        }
        return [...prev, next];
      });
      setMode("edit");
    } catch { /* ignore */ }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDrawingSave = (file: File, preview: string) => {
    if (!editingImageId) return;
    setImages((prev) =>
      prev.map((img) => {
        if (img.id !== editingImageId) return img;
        // Mask mode returns the mask itself — the source image stays untouched
        if (requiresMask) return { ...img, mask: preview };
        URL.revokeObjectURL(img.preview);
        return { ...img, file, preview };
      })
    );
    setEditingImageId(null);
  };

  const handleGenerate = async () => {
    if (selectedModel.isUpscaler) {
      if (images.length === 0) { setError("Molimo dodajte sliku za uvećanje"); return; }
    } else if (requiresMask) {
      if (images.length === 0) { setError("Molimo dodajte sliku"); return; }
      if (!images[0].mask) { setError("Nacrtajte masku za uklanjanje (ikona olovke na slici)"); return; }
    } else {
      if (mode === "edit" && images.length === 0) { setError("Molimo dodajte barem jednu sliku"); return; }
      if (!prompt.trim()) { setError("Molimo unesite prompt"); return; }
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setShowCompare(false);
    setProgressMessage(selectedModel.isUpscaler ? "Pokretanje uvećanja..." : requiresMask ? "Pokretanje uklanjanja..." : mode === "create" ? "Pokretanje kreacije..." : "Pripremanje...");

    // Capture the "before" image for comparison (the image marked as transfer target)
    if ((mode === "edit" || isPromptless) && images.length > 0) {
      const targetImage = images.find((img) => roles[img.id] === "target") ?? images[images.length - 1];
      setBeforeImage(targetImage?.preview ?? null);
    } else {
      setBeforeImage(null);
    }

    const resConfig = selectedModel.resolutions.find((r) => r.id === resolution);
    const imageSizeParam = resConfig?.autoValue
      ? resConfig.autoValue
      : isPromptless ? null : computeImageSize(aspectRatio, resolution, selectedModel.resolutions);

    try {
      const body: Record<string, unknown> = requiresMask ? {
        mode: "edit",
        model: selectedModelId,
      } : selectedModel.isUpscaler ? {
        mode: "edit",
        model: selectedModelId,
        upscaleFactor,
        outputFormat,
        // SeedVR params
        ...(selectedModel.hasTargetResolution ? { upscaleMode, targetResolution } : {}),
        ...(selectedModel.hasNoiseScale ? { noiseScale } : {}),
        // Topaz params
        ...(selectedModel.upscalerVariants ? { upscalerVariant } : {}),
        ...(selectedModel.hasFaceEnhancement ? { faceEnhancement, faceEnhancementStrength, faceEnhancementCreativity } : {}),
        ...(selectedModel.hasSubjectDetection ? { subjectDetection } : {}),
      } : {
        prompt: finalPrompt,
        imageSize: imageSizeParam,
        safetyChecker,
        mode,
        model: selectedModelId,
        aspectRatio,
        resolution,
        ...(selectedModel.hasEnhancePrompt ? { enhancePromptMode } : {}),
        ...(selectedModel.maxImages > 1 ? { numImages } : {}),
        ...(selectedModel.hasWebSearch ? { enableWebSearch: webSearch } : {}),
        ...(selectedModel.hasSafetyTolerance ? { safetyTolerance } : {}),
        ...(selectedModel.hasLimitGenerations ? { limitGenerations } : {}),
        ...(selectedModel.hasNegativePrompt
          ? { negativePrompt: [negativePrompt.trim(), negativeAdditions].filter(Boolean).join(", ") }
          : {}),
        ...(selectedModel.hasOutputFormat ? { generationOutputFormat } : {}),
        ...(selectedModel.hasPromptExpansion ? { enablePromptExpansion } : {}),
        ...(selectedModel.hasQuality ? { quality } : {}),
        ...(selectedModel.hasSeed && seed.trim() !== "" && Number.isFinite(Number(seed))
          ? { seed: Math.trunc(Number(seed)) }
          : {}),
        ...(selectedPersonId ? { personId: selectedPersonId } : {}),
        ...(selectedGarmentId ? { garmentId: selectedGarmentId } : {}),
      };

      if (requiresMask) {
        body.images = [await fileToBase64(images[0].file)];
        body.mask = images[0].mask;
      } else if (mode === "edit" || selectedModel.isUpscaler) {
        // Transfer roles decide the order: source garments first, target person last
        const sendImages = selectedModel.isUpscaler ? images : orderedImages;
        const base64Images = await Promise.all(sendImages.map((img) => fileToBase64(img.file)));
        body.images = base64Images;
      }

      const response = await fetch("/api/fal/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Non-streaming error (e.g. 400/401)
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          throw new Error(data.error || "Failed to generate image");
        } catch (e) {
          if (e instanceof Error && e.message !== "Failed to generate image") throw e;
          throw new Error("Failed to generate image");
        }
      }

      // Read streaming NDJSON response
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let resultData: Record<string, unknown> | null = null;
      let hasPartials = false;
      const upscaleSizeStr = selectedModel.isUpscaler
        ? (selectedModel.hasTargetResolution && upscaleMode === "target" ? targetResolution : `${upscaleFactor}x`)
        : null;
      const upscalePromptStr = selectedModel.isUpscaler
        ? `Upscale ${upscaleSizeStr}`
        : null;

      let lastPartialUrl: string | null = null;

      const handleMsg = (msg: Record<string, unknown>) => {
        if (msg.progress) {
          setProgressMessage(msg.progress as string);
        } else if (msg.error) {
          throw new Error(msg.error as string);
        } else if (msg.partial) {
          // Incremental result (batch upscale) — add to gallery immediately
          hasPartials = true;
          const p = msg.partial as { imageUrl: string; imageId?: number };
          lastPartialUrl = p.imageUrl;
          const now = new Date().toISOString().replace("Z", "").replace("T", " ").slice(0, 19);
          setGalleryImages((prev) => [
            {
              id: p.imageId ?? `temp-${Date.now()}`,
              prompt: upscalePromptStr || "Upscale",
              imageUrl: p.imageUrl,
              imageSize: upscaleSizeStr || "",
              createdAt: now,
              modelName: selectedModel.name,
            } as GalleryImage,
            ...prev,
          ]);
        } else if (msg.result) {
          resultData = msg.result as Record<string, unknown>;
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop()!; // keep incomplete last line

        for (const line of lines) {
          if (!line.trim()) continue;
          handleMsg(JSON.parse(line));
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        handleMsg(JSON.parse(buffer));
      }

      if (!resultData && !hasPartials) throw new Error("No result received");

      // For batch upscale: show last upscaled image as the main result and update duration
      if (hasPartials && lastPartialUrl) {
        setResult(lastPartialUrl);
        if (resultData) {
          const rd = resultData as Record<string, unknown>;
          const dur = rd.durationMs as number | undefined;
          if (dur != null) {
            setGalleryImages((prev) => prev.map((img) =>
              img.durationMs == null && img.modelName === selectedModel.name
                ? { ...img, durationMs: dur }
                : img
            ));
          }
        }
      }

      // Skip gallery updates if partials already added images incrementally
      if (!hasPartials && resultData) {
        const data = resultData as Record<string, unknown>;
        const imageSizeStr = selectedModel.isUpscaler
          ? upscaleSizeStr || `${upscaleFactor}x`
          : requiresMask
            ? "mask"
            : typeof imageSizeParam === "string"
              ? imageSizeParam
              : `${(imageSizeParam as { width: number; height: number }).width}x${(imageSizeParam as { width: number; height: number }).height}`;
        const promptStr = selectedModel.isUpscaler
          ? upscalePromptStr || `Upscale ${upscaleFactor}x`
          : requiresMask
            ? "Uklanjanje objekta"
            : prompt;
        const now = new Date().toISOString().replace("Z", "").replace("T", " ").slice(0, 19);

        const resDurationMs = data.durationMs as number | undefined;
        const resModelName = (data.modelName as string | undefined) || selectedModel.name;
        const resSeed = typeof data.seed === "number" ? data.seed : undefined;

        if (data.imageUrls && (data.imageUrls as string[]).length > 0) {
          const urls = data.imageUrls as string[];
          const ids = (data.imageIds as number[]) ?? [];
          setResult(urls[0]);
          setGalleryImages((prev) => [
            ...urls.map((url: string, i: number) => ({
              id: ids[i] ?? `temp-${Date.now()}-${i}`,
              prompt: promptStr,
              imageUrl: url,
              imageSize: imageSizeStr,
              createdAt: now,
              modelName: resModelName,
              durationMs: resDurationMs,
              seed: resSeed,
            })),
            ...prev,
          ]);
        } else if (data.imageUrl) {
          setResult(data.imageUrl as string);
          if (data.imageId) {
            setGalleryImages((prev) => [
              {
                id: data.imageId as number,
                prompt: promptStr,
                imageUrl: data.imageUrl as string,
                imageSize: imageSizeStr,
                createdAt: now,
                modelName: resModelName,
                durationMs: resDurationMs,
                seed: resSeed,
              },
              ...prev,
            ]);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
      setProgressMessage("");
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    try {
      const res = await fetch(result);
      const blob = await res.blob();
      const mime = blob.type;
      const ext = mime.includes("jpeg") || mime.includes("jpg") ? "jpg" : mime.includes("webp") ? "webp" : mime.includes("gif") ? "gif" : "png";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lunatik-generated.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(result, "_blank");
    }
  };

  const handleQuickUpscale = async () => {
    if (!result || isUpscaling) return;
    setIsUpscaling(true);
    setBeforeImage(result);
    setShowCompare(false);

    try {
      const body: Record<string, unknown> = {
        mode: "edit",
        model: "topaz",
        upscaleFactor: 2,
        outputFormat: "jpeg",
        upscalerVariant: "Standard V2",
        faceEnhancement: true,
        faceEnhancementStrength: 0.8,
        faceEnhancementCreativity: 0,
        subjectDetection: "All",
        images: [result],
      };

      const response = await fetch("/api/fal/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          throw new Error(data.error || "Failed to upscale image");
        } catch (e) {
          if (e instanceof Error && e.message !== "Failed to upscale image") throw e;
          throw new Error("Failed to upscale image");
        }
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let resultData: Record<string, unknown> | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop()!;
        for (const line of lines) {
          if (!line.trim()) continue;
          const msg = JSON.parse(line);
          if (msg.error) throw new Error(msg.error);
          else if (msg.result) resultData = msg.result;
        }
      }
      if (buffer.trim()) {
        const msg = JSON.parse(buffer);
        if (msg.error) throw new Error(msg.error);
        else if (msg.result) resultData = msg.result;
      }

      if (!resultData) throw new Error("No result received");
      const data = resultData as Record<string, unknown>;
      if (data.imageUrl) {
        setResult(data.imageUrl as string);
        const now = new Date().toISOString().replace("Z", "").replace("T", " ").slice(0, 19);
        if (data.imageId) {
          setGalleryImages((prev) => [
            {
              id: data.imageId as number,
              prompt: "Upscale 2x",
              imageUrl: data.imageUrl as string,
              imageSize: "2x",
              createdAt: now,
              modelName: (data.modelName as string) || "Topaz",
              durationMs: data.durationMs as number | undefined,
            },
            ...prev,
          ]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upscale failed");
    } finally {
      setIsUpscaling(false);
    }
  };

  const filteredRatios = ASPECT_RATIOS.filter((r) => selectedModel.aspectRatios.includes(r.id));
  const isAutoRes = !!selectedModel.resolutions.find((r) => r.id === resolution)?.autoValue;
  const currentSize = selectedModel.resolutions.length > 0
    ? computeImageSize(aspectRatio, resolution, selectedModel.resolutions)
    : { width: 0, height: 0 };
  // 4K costs 2x on every model that offers it, and is overkill for web/e-commerce
  const currentRes = selectedModel.resolutions.find((r) => r.id === resolution);
  const cheaperRes = selectedModel.resolutions.find((r) => r.maxSide === 2048);
  const showResWarning = !selectedModel.isUpscaler && (currentRes?.maxSide ?? 0) >= 4096;

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="px-5 py-2.5 flex items-center gap-3 shrink-0 backdrop-blur-xl bg-card/80 border-b border-border" style={{ boxShadow: "var(--effect-glow-header)" }}>
        <ImageIcon className="w-4 h-4 text-primary" />
        <h1 className="font-display text-base font-bold tracking-tight">Lunatik Studio</h1>
        <a href="/workflow" className="text-xs px-2 py-1 rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
          Workflow
        </a>

        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            title={theme === "dark" ? "Prebaci na svijetli mod" : "Prebaci na tamni mod"}
          >
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-2">
            <User className="w-3 h-3" />
            <span>{username}</span>
          </div>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => setShowAdmin(true)} className="h-7 px-2 text-xs text-muted-foreground hover:text-primary">
              <Shield className="w-3.5 h-3.5 mr-1" />
              Admin panel
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setShowSpending(true)} className="h-7 px-2 text-xs text-muted-foreground hover:text-primary">
            <DollarSign className="w-3.5 h-3.5 mr-1" />
            Potrošnja
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowLibrary(true)} className="h-7 px-2 text-xs text-muted-foreground hover:text-primary">
            <BookOpen className="w-3.5 h-3.5 mr-1" />
            Biblioteka
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout} className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
            <LogOut className="w-3.5 h-3.5 mr-1" />
            Odjava
          </Button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel - Controls (fixed width, single column always) */}
        <aside className="w-[360px] shrink-0 border-r border-border flex flex-col backdrop-blur-xl bg-card/80">
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-5">

              {/* Model + Mode section */}
              <div className="space-y-3">
                {/* Model picker — searchable combobox */}
                <Popover open={modelPickerOpen} onOpenChange={setModelPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      role="combobox"
                      aria-expanded={modelPickerOpen}
                      className="w-full flex items-center gap-2 px-2.5 py-2 text-left rounded-lg border border-border/60 bg-secondary/15 hover:bg-muted/50 hover:border-border transition-all"
                    >
                      <div className="w-1.5 h-1.5 rounded-full shrink-0 bg-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-medium leading-tight truncate text-foreground">
                          {selectedModel.name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-muted-foreground/60 leading-tight truncate">
                            {selectedModel.description}
                          </span>
                          <span className="text-[9px] leading-none tracking-tight text-muted-foreground/35 shrink-0">
                            {"$".repeat(priceTier(selectedModel.costPerImage))}
                          </span>
                        </div>
                      </div>
                      <ChevronsUpDown className="w-3 h-3 shrink-0 text-muted-foreground/50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[328px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Pretraži modele..." className="text-[11px]" />
                      <CommandList>
                        <CommandEmpty className="py-4 text-center text-[11px] text-muted-foreground">
                          Nema rezultata.
                        </CommandEmpty>
                        <CommandGroup>
                          {MODELS.map((model) => {
                            const isActive = selectedModelId === model.id;
                            return (
                              <CommandItem
                                key={model.id}
                                value={`${model.name} ${model.description}`}
                                onSelect={() => {
                                  handleModelChange(model.id);
                                  setModelPickerOpen(false);
                                }}
                                className="gap-2"
                              >
                                <Check
                                  className={`w-3 h-3 shrink-0 ${isActive ? "opacity-100 text-primary" : "opacity-0"}`}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className={`text-[11px] font-medium leading-tight truncate ${isActive ? "text-primary" : "text-foreground"}`}>
                                    {model.name}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground/60 leading-tight truncate">
                                    {model.description}
                                  </div>
                                </div>
                                <span className="text-[9px] leading-none tracking-tight text-muted-foreground/35 shrink-0">
                                  {"$".repeat(priceTier(model.costPerImage))}
                                </span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Mode toggle — compact segmented control (hidden for promptless models) */}
                {selectedModel.isUpscaler ? (
                  <div className="flex rounded-lg bg-secondary/40 p-0.5">
                    <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded-md bg-card text-foreground shadow-sm">
                      <ArrowUpCircle className="w-3 h-3" />
                      Uvećaj
                    </div>
                  </div>
                ) : requiresMask ? (
                  <div className="flex rounded-lg bg-secondary/40 p-0.5">
                    <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded-md bg-card text-foreground shadow-sm">
                      <Eraser className="w-3 h-3" />
                      Ukloni objekat
                    </div>
                  </div>
                ) : (
                <div className="flex rounded-lg bg-secondary/40 p-0.5 gap-0.5">
                  <button
                    onClick={() => selectedModel.supportsCreate && setMode("create")}
                    disabled={!selectedModel.supportsCreate}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded-md transition-all ${
                      mode === "create"
                        ? "bg-card text-foreground shadow-sm"
                        : !selectedModel.supportsCreate
                        ? "opacity-25 cursor-not-allowed text-muted-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    title={!selectedModel.supportsCreate ? `${selectedModel.name} podržava samo uređivanje` : undefined}
                  >
                    <Sparkles className="w-3 h-3" />
                    Kreiraj
                  </button>
                  <button
                    onClick={() => selectedModel.supportsEdit && setMode("edit")}
                    disabled={!selectedModel.supportsEdit}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded-md transition-all ${
                      mode === "edit"
                        ? "bg-card text-foreground shadow-sm"
                        : !selectedModel.supportsEdit
                        ? "opacity-25 cursor-not-allowed text-muted-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    title={!selectedModel.supportsEdit ? `${selectedModel.name} podržava samo kreiranje` : undefined}
                  >
                    <PenTool className="w-3 h-3" />
                    Uredi
                  </button>
                </div>
                )}
              </div>

              {/* Person/Garment Picker (edit mode only, not for promptless models) */}
              {mode === "edit" && !isPromptless && (
                <PersonGarmentPicker
                  key={pickerKey}
                  selectedPersonId={selectedPersonId}
                  selectedGarmentId={selectedGarmentId}
                  onSelectPerson={setSelectedPersonId}
                  onSelectGarment={setSelectedGarmentId}
                  onOpenLibrary={() => setShowLibrary(true)}
                />
              )}

              {/* Reference images — horizontal strip (edit mode + promptless models) */}
              {(mode === "edit" || isPromptless) && (
                <div className="space-y-2">
                  <span className="text-[11px] font-medium text-muted-foreground">{selectedModel.isUpscaler ? "Slika za uvećanje" : requiresMask ? "Slika za obradu" : "Referentne slike"}</span>
                  <div className="flex gap-2 items-start">
                    {/* Upload button */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      className="w-16 h-16 shrink-0 border border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all hover:border-primary/50 hover:bg-primary/5"
                    >
                      <ImagePlus className="w-4 h-4 text-muted-foreground/50" />
                      <span className="text-[9px] text-muted-foreground/50 mt-0.5">Dodaj</span>
                      <input ref={fileInputRef} type="file" accept="image/*" multiple={!requiresMask} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} className="hidden" />
                    </div>
                    {/* Thumbnail strip */}
                    {images.length > 0 && (
                      <div className="flex gap-1.5 flex-wrap min-w-0">
                        {images.map((img) => {
                          const role = roles[img.id];
                          const showRoles = mode === "edit" && !isPromptless && images.length >= 2;
                          return (
                          <div
                            key={img.id}
                            className={`relative group w-16 h-16 rounded-lg overflow-hidden border border-border shrink-0 ${
                              !showRoles ? "" : role === "product" ? "ring-1 ring-amber-400/50" : role === "target" ? "ring-1 ring-primary/50" : ""
                            }`}
                          >
                            <img src={img.preview} alt="Reference" className="w-full h-full object-cover" />
                            {requiresMask && img.mask && (
                              <>
                                <img
                                  src={img.mask}
                                  alt=""
                                  className="absolute inset-0 w-full h-full object-cover mix-blend-screen opacity-50 pointer-events-none"
                                />
                                <span className="absolute top-0.5 left-0.5 z-10 px-1 rounded-full bg-primary text-primary-foreground text-[8px] leading-[14px]">
                                  maska
                                </span>
                              </>
                            )}
                            {showRoles && (
                              <button
                                onClick={() => setRoles((prev) => cycleRole(prev, img.id))}
                                title={
                                  role === "product"
                                    ? "Proizvod koji se prenosi — klik za primaoca"
                                    : role === "target"
                                    ? "Primalac transfera — klik za uklanjanje uloge"
                                    : "Bez uloge — klik za proizvod"
                                }
                                className={`absolute top-0.5 left-0.5 z-10 w-4 h-4 rounded-full flex items-center justify-center text-[8px] leading-none transition-all ${
                                  role === "product"
                                    ? "bg-amber-400 text-amber-950"
                                    : role === "target"
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-black/50 text-white/60 opacity-0 group-hover:opacity-100 ring-1 ring-white/30"
                                }`}
                              >
                                {role === "product" ? (
                                  <Shirt className="w-2.5 h-2.5" />
                                ) : role === "target" ? (
                                  <UserRound className="w-2.5 h-2.5" />
                                ) : null}
                              </button>
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => setEditingImageId(img.id)}
                                className="bg-black/60 text-white rounded-full p-1 hover:bg-primary/80 transition-colors"
                                title={requiresMask ? (img.mask ? "Uredi masku" : "Nacrtaj masku") : "Crtaj na slici"}
                              >
                                {requiresMask ? <Eraser className="w-2.5 h-2.5" /> : <Pencil className="w-2.5 h-2.5" />}
                              </button>
                              <button
                                onClick={() => removeImage(img.id)}
                                className="bg-black/60 text-white rounded-full p-1 hover:bg-destructive/80 transition-colors"
                                title="Ukloni"
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {mode === "edit" && !isPromptless && images.length >= 2 && (
                    <p className="text-[10px] text-muted-foreground/50 leading-tight">
                      Klikni bedž na slici: <Shirt className="w-2.5 h-2.5 inline text-amber-400" /> proizvod koji se prenosi ·{" "}
                      <UserRound className="w-2.5 h-2.5 inline text-primary" /> primalac transfera
                    </p>
                  )}
                  {requiresMask && (
                    <p className="text-[10px] text-muted-foreground/50 leading-tight">
                      {images.length === 0
                        ? "Dodaj jednu sliku, pa preboj ono što se uklanja."
                        : images[0].mask
                        ? "Maska je spremna. Klikni gumicu na slici za izmjenu."
                        : "Klikni gumicu na slici i preboj objekat koji se uklanja."}
                    </p>
                  )}
                </div>
              )}

              {/* Smart Prompt Wizard (edit mode with images, not for promptless models) */}
              {mode === "edit" && !isPromptless && images.length > 0 && (
                showSmartPrompt ? (
                  <SmartPromptWizard
                    images={images}
                    onUsePrompt={(p) => setPrompt(p)}
                    onClose={() => setShowSmartPrompt(false)}
                    savedPersonId={selectedPersonId}
                    savedGarmentId={selectedGarmentId}
                    initialGarmentIds={images.filter((i) => roles[i.id] === "product").map((i) => i.id)}
                    initialTargetId={images.find((i) => roles[i.id] === "target")?.id ?? null}
                  />
                ) : (
                  <button
                    onClick={() => setShowSmartPrompt(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium rounded-lg border border-dashed border-primary/30 text-primary/70 hover:bg-primary/5 hover:border-primary/50 transition-all"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Smart Prompt
                  </button>
                )
              )}

              {/* Prompt (hidden for promptless models) */}
              {!isPromptless && (
              <div className="space-y-2">
                <span className="text-[11px] font-medium text-muted-foreground">Opis</span>
                <Textarea
                  placeholder={mode === "create" ? "Opišite sliku koju želite kreirati..." : "Opišite izmjenu koju želite..."}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={3}
                  className="bg-secondary/20 border-border/60 focus:border-primary/40 text-sm resize-none"
                />
              </div>
              )}

              {/* Prompt guards (edit mode only) */}
              {guardsApply && <PromptGuards active={guards} onChange={updateGuards} />}

              {/* Output settings card (mask removal has no options to configure) */}
              {!requiresMask && (
              <div className="rounded-xl border border-border/60 bg-secondary/15 p-3 space-y-3.5">
                <div className="flex items-center gap-1.5">
                  <Settings2 className="w-3 h-3 text-muted-foreground/60" />
                  <span className="text-[11px] font-medium text-muted-foreground">{selectedModel.isUpscaler ? "Postavke uvećanja" : "Postavke izlaza"}</span>
                </div>

                {selectedModel.isUpscaler ? (
                  <>
                    {/* Upscaler model variant picker */}
                    {selectedModel.upscalerVariants && (
                      <div className="space-y-1.5">
                        <span className="text-[10px] text-muted-foreground/70">Model</span>
                        <div className="grid grid-cols-2 rounded-md bg-secondary/40 p-0.5 gap-0.5">
                          {selectedModel.upscalerVariants.map((v) => {
                            const isActive = upscalerVariant === v.id;
                            return (
                              <button
                                key={v.id}
                                onClick={() => setUpscalerVariant(v.id)}
                                className={`py-1 px-1 text-[10px] font-medium rounded-md transition-all ${
                                  isActive
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {v.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* SeedVR: Upscale mode — target vs factor */}
                    {selectedModel.hasTargetResolution && (
                      <div className="space-y-1.5">
                        <span className="text-[10px] text-muted-foreground/70">Način uvećanja</span>
                        <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                          {(["target", "factor"] as const).map((m) => {
                            const isActive = upscaleMode === m;
                            return (
                              <button
                                key={m}
                                onClick={() => setUpscaleMode(m)}
                                className={`flex-1 py-1 text-[11px] font-medium rounded-md transition-all ${
                                  isActive
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {m === "target" ? "Ciljna rezolucija" : "Faktor"}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* SeedVR: Target resolution picker */}
                    {selectedModel.hasTargetResolution && upscaleMode === "target" && selectedModel.upscaleTargetResolutions && (
                      <div className="space-y-1.5">
                        <span className="text-[10px] text-muted-foreground/70">Ciljna rezolucija</span>
                        <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                          {selectedModel.upscaleTargetResolutions.map((r) => {
                            const isActive = targetResolution === r.id;
                            return (
                              <button
                                key={r.id}
                                onClick={() => setTargetResolution(r.id)}
                                className={`flex-1 py-1 text-[11px] font-medium rounded-md transition-all ${
                                  isActive
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {r.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Upscale factor slider (SeedVR: only in factor mode, Topaz: always) */}
                    {(!selectedModel.hasTargetResolution || upscaleMode === "factor") && (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground/70">Faktor uvećanja</span>
                          <span className="text-[11px] font-medium tabular-nums text-foreground">{upscaleFactor}x</span>
                        </div>
                        <input
                          type="range"
                          min={1}
                          max={selectedModel.maxUpscaleFactor ?? 4}
                          step={1}
                          value={upscaleFactor}
                          onChange={(e) => setUpscaleFactor(parseInt(e.target.value))}
                          className="w-full accent-primary h-1"
                        />
                      </div>
                    )}

                    {/* SeedVR: Noise scale slider */}
                    {selectedModel.hasNoiseScale && (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground/70">Šum</span>
                          <span className="text-[11px] font-medium tabular-nums text-foreground">{noiseScale.toFixed(2)}</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={noiseScale}
                          onChange={(e) => setNoiseScale(parseFloat(e.target.value))}
                          className="w-full accent-primary h-1"
                        />
                      </div>
                    )}

                    {/* Subject detection picker */}
                    {selectedModel.hasSubjectDetection && (
                      <div className="space-y-1.5">
                        <span className="text-[10px] text-muted-foreground/70">Detekcija subjekta</span>
                        <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                          {(["All", "Foreground", "Background"] as const).map((sd) => {
                            const isActive = subjectDetection === sd;
                            const labels: Record<string, string> = { All: "Sve", Foreground: "Prednji plan", Background: "Pozadina" };
                            return (
                              <button
                                key={sd}
                                onClick={() => setSubjectDetection(sd)}
                                className={`flex-1 py-1 text-[11px] font-medium rounded-md transition-all ${
                                  isActive
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {labels[sd]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Face enhancement toggle + sliders */}
                    {selectedModel.hasFaceEnhancement && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground/70">Poboljšanje lica</span>
                          <Switch checked={faceEnhancement} onCheckedChange={setFaceEnhancement} />
                        </div>
                        {faceEnhancement && (
                          <>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-muted-foreground/70">Jačina</span>
                                <span className="text-[11px] font-medium tabular-nums text-foreground">{faceEnhancementStrength.toFixed(1)}</span>
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.1}
                                value={faceEnhancementStrength}
                                onChange={(e) => setFaceEnhancementStrength(parseFloat(e.target.value))}
                                className="w-full accent-primary h-1"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-muted-foreground/70">Kreativnost</span>
                                <span className="text-[11px] font-medium tabular-nums text-foreground">{faceEnhancementCreativity.toFixed(1)}</span>
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.1}
                                value={faceEnhancementCreativity}
                                onChange={(e) => setFaceEnhancementCreativity(parseFloat(e.target.value))}
                                className="w-full accent-primary h-1"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Output format picker */}
                    {selectedModel.outputFormats && (
                      <div className="space-y-1.5">
                        <span className="text-[10px] text-muted-foreground/70">Format izlaza</span>
                        <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                          {selectedModel.outputFormats.map((f) => {
                            const isActive = outputFormat === f.id;
                            return (
                              <button
                                key={f.id}
                                onClick={() => setOutputFormat(f.id)}
                                className={`flex-1 py-1 text-[11px] font-medium rounded-md transition-all ${
                                  isActive
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {f.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                {/* Aspect ratio — compact grid (hidden when auto resolution is selected) */}
                {!isAutoRes && (
                <div className="space-y-1.5">
                  <span className="text-[10px] text-muted-foreground/70">Omjer slike</span>
                  <div className="grid grid-cols-5 gap-1">
                    {filteredRatios.map((r) => {
                      const isActive = aspectRatio === r.id;
                      const iconW = (r.w / Math.max(r.w, r.h)) * 14;
                      const iconH = (r.h / Math.max(r.w, r.h)) * 14;
                      return (
                        <button
                          key={r.id}
                          onClick={() => setAspectRatio(r.id)}
                          className={`flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] rounded-md transition-all ${
                            isActive
                              ? "bg-primary/12 text-primary ring-1 ring-primary/30"
                              : "hover:bg-muted/60 text-muted-foreground"
                          }`}
                        >
                          <div className="flex items-center justify-center w-4 h-4">
                            <div
                              className={`rounded-[2px] ${isActive ? "border-[1.5px] border-primary" : "border border-current opacity-40"}`}
                              style={{ width: iconW, height: iconH }}
                            />
                          </div>
                          <span className="leading-none">{r.label}</span>
                          {r.hint && <span className="leading-none text-[7px] text-muted-foreground/50">{r.hint}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Resolution — inline segmented (auto options are toggleable) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground/70">Rezolucija</span>
                    <span className="text-[10px] text-muted-foreground/50 tabular-nums">
                      {isAutoRes
                        ? "Auto"
                        : `${currentSize.width} x ${currentSize.height}`}
                    </span>
                  </div>
                  {selectedModel.resolutions.length <= 4 ? (
                    <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                      {selectedModel.resolutions.map((r) => {
                        const isActive = resolution === r.id;
                        return (
                          <button
                            key={r.id}
                            onClick={() => setResolution(isActive && r.autoValue ? "" : r.id)}
                            className={`flex-1 py-1 text-[11px] font-medium rounded-md transition-all ${
                              isActive
                                ? "bg-card text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {r.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-1">
                      {selectedModel.resolutions.map((r) => {
                        const isActive = resolution === r.id;
                        const hasAspect = r.aspectW && r.aspectH;
                        const iconW = hasAspect ? (r.aspectW! / Math.max(r.aspectW!, r.aspectH!)) * 14 : 0;
                        const iconH = hasAspect ? (r.aspectH! / Math.max(r.aspectW!, r.aspectH!)) * 14 : 0;
                        return (
                          <button
                            key={r.id}
                            onClick={() => setResolution(isActive && r.autoValue ? "" : r.id)}
                            className={`flex flex-col items-center justify-center gap-0.5 py-1.5 px-2 text-[10px] font-medium rounded-md transition-all ${
                              isActive
                                ? "bg-card text-foreground shadow-sm"
                                : "bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                            }`}
                          >
                            {hasAspect && (
                              <div className="flex items-center justify-center w-4 h-4">
                                <div
                                  className={`rounded-[2px] ${isActive ? "border-[1.5px] border-primary" : "border border-current opacity-40"}`}
                                  style={{ width: iconW, height: iconH }}
                                />
                              </div>
                            )}
                            <span className="leading-none">{r.label}</span>
                            {hasAspect && (
                              <span className="leading-none text-[8px] text-muted-foreground/50">{r.aspectW}:{r.aspectH}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {showResWarning && (
                    <div className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5">
                      <AlertTriangle className="w-3 h-3 shrink-0 mt-px text-destructive" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] leading-tight text-destructive">
                          4K troši 2× više nego što je potrebno. 2K je skroz OK za web i e-commerce.
                        </p>
                        {cheaperRes && (
                          <button
                            onClick={() => setResolution(cheaperRes.id)}
                            className="mt-1 text-[10px] font-medium text-destructive underline underline-offset-2 hover:no-underline"
                          >
                            Prebaci na {cheaperRes.label}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Enhance prompt — inline segmented */}
                {selectedModel.hasEnhancePrompt && (
                  <div className="space-y-1.5">
                    <span className="text-[10px] text-muted-foreground/70">Poboljšanje prompta</span>
                    <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                      {(["standard", "fast"] as const).map((m) => {
                        const isActive = enhancePromptMode === m;
                        return (
                          <button
                            key={m}
                            onClick={() => setEnhancePromptMode(m)}
                            className={`flex-1 py-1 text-[11px] font-medium rounded-md transition-all capitalize ${
                              isActive
                                ? "bg-card text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {m === "standard" ? "Standardno" : "Brzo"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Num images slider */}
                {selectedModel.maxImages > 1 && (() => {
                  const maxImages = selectedModel.maxImages;
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground/70">Slike</span>
                        <span className="text-[11px] font-medium tabular-nums text-foreground">{numImages}</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={maxImages}
                        step={1}
                        value={Math.min(numImages, maxImages)}
                        onChange={(e) => setNumImages(parseInt(e.target.value))}
                        className="w-full accent-primary h-1"
                      />
                    </div>
                  );
                })()}

                {/* Seed input */}
                {selectedModel.hasSeed && (
                  <div className="space-y-1.5">
                    <span className="text-[10px] text-muted-foreground/70">Seed</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={seed}
                        onChange={(e) => setSeed(e.target.value.replace(/[^0-9]/g, ""))}
                        placeholder="Nasumičan"
                        className="flex-1 min-w-0 h-7 px-2 rounded-md bg-muted/30 border border-border/50 text-[11px] tabular-nums text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50"
                      />
                      <button
                        onClick={() => setSeed(String(Math.floor(Math.random() * 2147483647)))}
                        className="h-7 w-7 flex items-center justify-center rounded-md bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Nasumičan seed"
                      >
                        <Dices className="w-3.5 h-3.5" />
                      </button>
                      {seed !== "" && (
                        <button
                          onClick={() => setSeed("")}
                          className="h-7 w-7 flex items-center justify-center rounded-md bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          title="Očisti seed"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Safety checker toggle */}
                {selectedModel.hasSafetyChecker && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground/70">Sigurnosna provjera</span>
                    <Switch checked={safetyChecker} onCheckedChange={setSafetyChecker} />
                  </div>
                )}

                {/* Safety tolerance slider (1-6) */}
                {selectedModel.hasSafetyTolerance && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted-foreground/70">Tolerancija sigurnosti</span>
                      <span className="text-[11px] font-medium tabular-nums text-foreground">{safetyTolerance}</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={6}
                      step={1}
                      value={parseInt(safetyTolerance)}
                      onChange={(e) => setSafetyTolerance(e.target.value)}
                      className="w-full accent-primary h-1"
                    />
                    <div className="flex justify-between text-[9px] text-muted-foreground/40">
                      <span>Strožije</span>
                      <span>Blaže</span>
                    </div>
                  </div>
                )}

                {/* Limit generations toggle */}
                {selectedModel.hasLimitGenerations && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground/70">Ograniči generacije</span>
                    <Switch checked={limitGenerations} onCheckedChange={setLimitGenerations} />
                  </div>
                )}

                {/* Prompt expansion toggle */}
                {selectedModel.hasPromptExpansion && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Wand2 className="w-3 h-3 text-muted-foreground/50" />
                      <span className="text-[10px] text-muted-foreground/70">Prompt ekspanzija</span>
                    </div>
                    <Switch checked={enablePromptExpansion} onCheckedChange={setEnablePromptExpansion} />
                  </div>
                )}

                {/* Quality selector */}
                {selectedModel.hasQuality && selectedModel.qualityOptions && (
                  <div>
                    <span className="text-[10px] text-muted-foreground/70 mb-1 block">Kvalitet</span>
                    <div className="flex gap-1">
                      {selectedModel.qualityOptions.map((q) => {
                        const isActive = quality === q.id;
                        return (
                          <button
                            key={q.id}
                            onClick={() => setQuality(q.id)}
                            className={`flex-1 py-1 rounded text-[10px] font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted"}`}
                          >
                            {q.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Generation output format */}
                {selectedModel.hasOutputFormat && selectedModel.generationOutputFormats && (
                  <div>
                    <span className="text-[10px] text-muted-foreground/70 mb-1 block">Output format</span>
                    <div className="flex gap-1">
                      {selectedModel.generationOutputFormats.map((f) => {
                        const isActive = generationOutputFormat === f.id;
                        return (
                          <button
                            key={f.id}
                            onClick={() => setGenerationOutputFormat(f.id)}
                            className={`flex-1 py-1 rounded text-[10px] font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted"}`}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Negative prompt */}
                {selectedModel.hasNegativePrompt && (
                  <div>
                    <span className="text-[10px] text-muted-foreground/70 mb-1 block">Negativni prompt</span>
                    <Textarea
                      value={negativePrompt}
                      onChange={(e) => setNegativePrompt(e.target.value)}
                      placeholder="Šta izbjegavati..."
                      className="min-h-[48px] text-xs resize-none bg-muted/30 border-border/50"
                      rows={2}
                    />
                  </div>
                )}

                {/* Web search toggle */}
                {selectedModel.hasWebSearch && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Globe className="w-3 h-3 text-muted-foreground/50" />
                      <span className="text-[10px] text-muted-foreground/70">Web pretraga</span>
                      <span className="text-[9px] text-muted-foreground/40">+${selectedModel.webSearchCostPerImage.toFixed(3)}/img</span>
                    </div>
                    <Switch checked={webSearch} onCheckedChange={setWebSearch} />
                  </div>
                )}
                  </>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Generate button — sticky bottom */}
          <div className="p-3 border-t border-border bg-card/80 backdrop-blur-xl">
            {error && <p className="text-[11px] text-destructive mb-1.5 px-0.5">{error}</p>}
            <Button
              onClick={handleGenerate}
              disabled={isLoading}
              className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg animate-glow-pulse"
              style={{ boxShadow: "var(--effect-glow-md)" }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                  {selectedModel.isUpscaler ? "Uvećavanje..." : requiresMask ? "Uklanjanje..." : "Generisanje..."}
                </>
              ) : (
                <span className="flex items-center gap-2">
                  {selectedModel.isUpscaler ? <ArrowUpCircle className="w-3.5 h-3.5" /> : requiresMask ? <Eraser className="w-3.5 h-3.5" /> : mode === "create" ? <Sparkles className="w-3.5 h-3.5" /> : <PenTool className="w-3.5 h-3.5" />}
                  {selectedModel.isUpscaler ? "Uvećaj" : requiresMask ? "Ukloni" : mode === "create" ? "Kreiraj" : "Generiši"}
                  <span className="text-[11px] opacity-60 tabular-nums">
                    {requiresMask
                      ? `$${selectedModel.costPerImage.toFixed(3)}`
                      : selectedModel.isUpscaler
                      ? `~$${(
                          selectedModel.hasTargetResolution && upscaleMode === "target"
                            ? (selectedModel.upscaleTargetResolutions?.find((r) => r.id === targetResolution)?.estimatedCost ?? selectedModel.costPerImage)
                            : selectedModel.costPerImage
                        ).toFixed(3)}`
                      : `$${(
                          (selectedModel.costPerImage
                            * (selectedModel.resolutions.find((r) => r.id === resolution)?.costMultiplier ?? 1)
                            + (selectedModel.hasWebSearch && webSearch ? selectedModel.webSearchCostPerImage : 0))
                          * (selectedModel.maxImages > 1 ? numImages : 1)
                        ).toFixed(3)}`
                    }
                  </span>
                </span>
              )}
            </Button>
          </div>
        </aside>

        {/* Right panel — Result + Gallery */}
        <main className="flex-1 flex flex-col min-h-0">
          {/* Result area */}
          <div className="flex-1 flex items-center justify-center p-4 min-h-0">
            {result ? (
              <div className="flex flex-col items-center gap-3 max-w-full h-full min-h-0">
                {showCompare && beforeImage ? (
                  <div className="flex items-center justify-center max-w-full min-h-0 flex-1" style={{ boxShadow: "var(--effect-shadow-image)" }}>
                    <BeforeAfterSlider
                      beforeSrc={beforeImage}
                      afterSrc={result}
                      beforeLabel="Original"
                      afterLabel="Generisano"
                    />
                  </div>
                ) : (
                  <img
                    src={result}
                    alt="Generated result"
                    className="max-w-full min-h-0 flex-1 object-contain rounded-xl"
                    style={{ boxShadow: "var(--effect-shadow-image)" }}
                  />
                )}
                <div className="flex gap-2 shrink-0">
                  {beforeImage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCompare(!showCompare)}
                      className={`border-border hover:border-primary/40 hover:text-primary ${showCompare ? "bg-primary/10 text-primary border-primary/30" : ""}`}
                    >
                      <Columns2 className="w-4 h-4 mr-2" />
                      {showCompare ? "Sakrij uporedbu" : "Uporedi"}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={handleDownload} className="border-border hover:border-primary/40 hover:text-primary">
                    <Download className="w-4 h-4 mr-2" />
                    Preuzmi
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleQuickUpscale}
                    disabled={isUpscaling || isLoading}
                    className="border-border hover:border-primary/40 hover:text-primary"
                  >
                    {isUpscaling ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowUpCircle className="w-4 h-4 mr-2" />
                    )}
                    {isUpscaling ? "Uvećavanje..." : "Uvećaj 2x"}
                  </Button>
                </div>
              </div>
            ) : isLoading ? (
              <div className="flex flex-col items-center gap-6">
                {/* Neon infinity trace animation */}
                <div className="relative flex items-center justify-center" style={{ width: 280, height: 140 }}>
                  <svg
                    viewBox="-5 -5 110 60"
                    fill="none"
                    style={{
                      width: "100%",
                      height: "100%",
                      overflow: "visible",
                      filter: `drop-shadow(0 0 12px ${neonColor})`,
                    }}
                  >
                    <path
                      ref={infinityPathRef}
                      d="M 50,25 C 35,0 0,0 0,25 C 0,50 35,50 50,25 C 65,0 100,0 100,25 C 100,50 65,50 50,25 Z"
                      stroke={neonColor}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="animate-trace-infinity"
                      style={{
                        "--infinity-path-length": infinityPathLength,
                        strokeDasharray: `${infinityPathLength * 0.25} ${infinityPathLength * 0.75}`,
                        strokeDashoffset: infinityPathLength,
                        transition: "stroke 0.4s ease",
                      } as React.CSSProperties}
                    />
                  </svg>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <p
                    className="text-sm font-medium transition-all duration-400"
                    style={{ color: neonColor, textShadow: `0 0 10px ${neonColor}40` }}
                  >
                    {progressMessage || (mode === "create" ? "Kreiranje vaše slike" : "Generisanje vaše slike")}
                  </p>
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full animate-fade-pulse"
                        style={{
                          animationDelay: `${i * 0.4}s`,
                          backgroundColor: neonColor,
                          opacity: 0.5,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground/30">
                <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                <p className="text-xs">Rezultat će se pojaviti ovdje</p>
              </div>
            )}
          </div>

          {/* Gallery section */}
          {galleryImages.length > 0 && (
            <div className="border-t border-border shrink-0 max-h-[280px] overflow-y-auto" style={{ boxShadow: "var(--effect-glow-gallery)" }}>
              <div className="p-3">
                <span className="text-[11px] font-medium text-muted-foreground mb-2 block">
                  Galerija ({galleryImages.length})
                </span>
                <ImageGallery
                  images={galleryImages}
                  onOpenLibraryPerson={(id) => { setLibraryFocusPersonId(id); setLibraryFocusGarmentId(null); setShowLibrary(true); }}
                  onOpenLibraryGarment={(id) => { setLibraryFocusGarmentId(id); setLibraryFocusPersonId(null); setShowLibrary(true); }}
                  onUseAsReference={addImageFromUrl}
                  onUpscale={addImageForUpscale}
                />
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Drawing editor modal */}
      <DrawingEditor
        open={!!editingImageId}
        imageSrc={editingImage?.preview ?? ""}
        mode={requiresMask ? "mask" : "draw"}
        onSave={handleDrawingSave}
        onClose={() => setEditingImageId(null)}
      />

      {/* Admin panel modal */}
      {showAdmin && (
        <AdminPanel
          onClose={() => setShowAdmin(false)}
          currentUsername={username}
        />
      )}

      {/* Spending stats modal */}
      {showSpending && (
        <SpendingStats
          onClose={() => setShowSpending(false)}
          isAdmin={isAdmin}
        />
      )}

      {/* Person/Garment Library modal */}
      {showLibrary && (
        <PersonGarmentLibrary
          onClose={() => { setShowLibrary(false); setPickerKey((k) => k + 1); setLibraryFocusPersonId(null); setLibraryFocusGarmentId(null); }}
          focusPersonId={libraryFocusPersonId}
          focusGarmentId={libraryFocusGarmentId}
        />
      )}
    </div>
  );
}
